// Settings → Purchasing (docs/plans/po-approvals.md, purchase-requests.md): whether purchase
// orders and purchase requests need approval, the steps by amount for each, and how much each
// person may approve. Owners and admins, and only with purchase_control. Board
// POApprovalSettings, POApprovalLimit.
import type { RowOf } from '@ims/data'
import { tenantApi } from '@ims/remote'
import {
  Avatar,
  Button,
  Card,
  ConfirmDialog,
  Modal,
  Select,
  SkeletonForm,
  Switch,
  cx,
  useToast,
} from '@ims/ui'
import { Plus, Trash2 } from '@ims/ui/icons'
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { MoneyInput } from '../components.tsx'
import { errorMessage, useLive, useOnline, useSettings } from '../hooks.ts'
import { ROLE_LABEL, roleAtLeast, type MemberRole } from '../lib/approvals.ts'
import { money } from '../lib/format.ts'
import { deleteOp, insertOp, updateOp } from '../lib/rows.ts'
import { useSession } from '../session.tsx'
import { useCan, useRepo, useWorkspace } from '../workspace.tsx'
import { SettingsPart } from './settings-part.tsx'

type Rule = RowOf<'approval_rules'>
type Role = Rule['approver_role']

interface Approver {
  user_id: string
  role: MemberRole
  approval_limit_paisa: number | null
  name: string
}

/** The words of each kind of document's approval. */
const DOCS = {
  purchase_order: {
    title: 'Approval of purchase orders',
    about: 'Orders above an amount wait for the right person before they go to the supplier.',
    label: 'Purchase orders need approval',
    on: 'An order a step applies to is submitted, and sent once every step says yes.',
    off: 'Without it an order is sent as soon as it is saved.',
    stepsAbout:
      'Who approves, by the order’s total. Every step that applies must say yes, in order.',
    from: 'Orders from',
    prefix: '',
    a: 'An order',
    what: 'order',
    stopMessage:
      'New orders are sent as soon as they are saved. Orders waiting now can still be approved or cancelled.',
  },
  requisition: {
    title: 'Approval of purchase requests',
    about: 'Requests above an amount wait for the right person before anything is ordered.',
    label: 'Purchase requests need approval',
    on: 'A request a step applies to waits, and can be ordered once every step says yes.',
    off: 'Without it a request can be ordered as soon as it is submitted.',
    stepsAbout:
      'Who approves, by the request’s estimate. Every step that applies must say yes, in order.',
    from: 'Requests from',
    prefix: 'Request ',
    a: 'A request',
    what: 'request',
    stopMessage:
      'New requests can be ordered as soon as they are submitted. Requests waiting now can still be approved or cancelled.',
  },
} as const

export function PurchasingSettings() {
  const anyRules =
    (useLive((r) => r.list('approval_rules', { limit: 1 }), [], ['approval_rules']).data?.rows
      .length ?? 0) > 0
  return (
    <Card className="p-0">
      <ApprovalSteps docType="purchase_order" />
      <ApprovalSteps docType="requisition" />
      {anyRules && <ApprovalLimits />}
    </Card>
  )
}

function ApprovalSteps({ docType }: { docType: keyof typeof DOCS }) {
  const words = DOCS[docType]
  const repo = useRepo()
  const toast = useToast()
  const settings = useSettings()
  const { current } = useWorkspace()
  const canAdmin = useCan('admin')
  const [busy, setBusy] = useState(false)
  const [turningOff, setTurningOff] = useState(false)
  const rules =
    useLive(
      (r) =>
        r.list('approval_rules', {
          where: { doc_type: docType },
          orderBy: { column: 'level' },
          limit: 10,
        }),
      [docType],
      ['approval_rules'],
    ).data?.rows ?? null
  const commit = async (ops: Parameters<typeof repo.commit>[0], message?: string) => {
    setBusy(true)
    try {
      await repo.commit(ops)
      if (message) toast.success(message)
      return true
    } catch (error) {
      toast.error(errorMessage(error))
      return false
    } finally {
      setBusy(false)
    }
  }
  const newRule = (level: number, from: number, role: Role): Rule => {
    const now = new Date().toISOString()
    return {
      id: crypto.randomUUID(),
      tenant_id: current!.tenant_id,
      created_at: now,
      updated_at: now,
      deleted_at: null,
      doc_type: docType,
      level,
      min_amount_paisa: from,
      approver_role: role,
    }
  }
  if (!rules || !settings) return <SkeletonForm />
  const on = rules.length > 0
  const nextLevel = Math.max(0, ...rules.map((r) => r.level)) + 1
  const example = (amount: number) => {
    const n = rules.filter((r) => r.min_amount_paisa <= amount).length
    return n === 0 ? 'no step' : n === 1 ? 'step 1' : `steps 1 to ${n}`
  }
  return (
    <>
      <SettingsPart title={words.title} about={words.about}>
        <Switch
          label={words.label}
          description={on ? words.on : words.off}
          checked={on}
          disabled={!canAdmin || busy}
          onChange={(e) => {
            if (e.target.checked)
              void commit([insertOp('approval_rules', newRule(1, 0, 'manager'))], words.label)
            else setTurningOff(true)
          }}
        />
        {!canAdmin && (
          <p className="text-xs text-muted sm:col-span-2">Only owners and admins change this.</p>
        )}
      </SettingsPart>
      {on && (
        <SettingsPart title="Steps" about={words.stepsAbout}>
          <div className="sm:col-span-2">
            <div className="overflow-hidden rounded-xl border border-border">
              <div className="grid grid-cols-[4.5rem_minmax(0,1fr)_minmax(0,1fr)_2.5rem] gap-2.5 px-3 py-2 text-table-head text-subtle max-sm:hidden">
                <span>Step</span>
                <span>{words.from}</span>
                <span>Approved by</span>
                <span />
              </div>
              {rules.map((r, i) => (
                <div
                  key={r.id}
                  className="grid grid-cols-[4.5rem_minmax(0,1fr)_minmax(0,1fr)_2.5rem] items-center gap-2.5 border-t border-soft px-3 py-2 max-sm:grid-cols-[minmax(0,1fr)_2.5rem]"
                >
                  <b className="max-sm:col-span-2">Step {i + 1}</b>
                  <MoneyInput
                    aria-label={`${words.prefix}Step ${i + 1}: ${words.from.toLowerCase()}`}
                    prefix="Rs."
                    paisa={r.min_amount_paisa}
                    disabled={!canAdmin || busy}
                    onValue={(paisa) => {
                      if (paisa === null || paisa === r.min_amount_paisa) return
                      void commit([updateOp('approval_rules', r, { min_amount_paisa: paisa })])
                    }}
                  />
                  <Select
                    aria-label={`${words.prefix}Step ${i + 1}: approved by`}
                    value={r.approver_role}
                    disabled={!canAdmin || busy}
                    onChange={(e) =>
                      void commit([
                        updateOp('approval_rules', r, { approver_role: e.target.value as Role }),
                      ])
                    }
                  >
                    {(Object.keys(ROLE_LABEL) as Role[]).map((role) => (
                      <option key={role} value={role}>
                        {ROLE_LABEL[role]}
                      </option>
                    ))}
                  </Select>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Remove ${words.prefix.toLowerCase()}step ${i + 1}`}
                    disabled={!canAdmin || busy || rules.length === 1}
                    title={rules.length === 1 ? 'Turn approval off instead' : undefined}
                    onClick={() => void commit([deleteOp('approval_rules', r.id)], 'Step removed')}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              ))}
            </div>
            {canAdmin && nextLevel <= 5 && (
              <Button
                variant="secondary"
                className="mt-2.5"
                icon={<Plus className="size-4" />}
                disabled={busy}
                onClick={() =>
                  void commit(
                    [
                      insertOp(
                        'approval_rules',
                        newRule(
                          nextLevel,
                          Math.max(10_000_000, ...rules.map((r) => r.min_amount_paisa * 2)),
                          'owner',
                        ),
                      ),
                    ],
                    'Step added',
                  )
                }
              >
                Add a step
              </Button>
            )}
            <p className="mt-2.5 text-sm text-muted">
              {words.a} of {money(4_000_000, settings)} needs {example(4_000_000)}; one of{' '}
              {money(25_000_000, settings)} needs {example(25_000_000)}. The person who made{' '}
              {words.what === 'order' ? 'an order' : 'a request'} never approves it, and nobody
              approves two steps of one {words.what}.
            </p>
          </div>
        </SettingsPart>
      )}
      <ConfirmDialog
        open={turningOff}
        title="Stop asking for approval?"
        message={words.stopMessage}
        confirmLabel="Stop asking"
        busy={busy}
        onClose={() => setTurningOff(false)}
        onConfirm={async () => {
          if (
            await commit(
              rules.map((r) => deleteOp('approval_rules', r.id)),
              'Approval is off',
            )
          )
            setTurningOff(false)
        }}
      />
    </>
  )
}

/** How much each manager, admin and owner may approve, on top of their role. Online. */
function ApprovalLimits() {
  const { platform } = useSession()
  const { current } = useWorkspace()
  const settings = useSettings()
  const online = useOnline()
  const canAdmin = useCan('admin')
  const [people, setPeople] = useState<Approver[] | null>(null)
  const [editing, setEditing] = useState<Approver | null>(null)
  const tenantId = current!.tenant_id
  const load = useCallback(async () => {
    const supabase = platform.supabase
    const { data: rows } = await supabase
      .from('memberships')
      .select('user_id, role, approval_limit_paisa')
      .eq('tenant_id', tenantId)
    const list = (rows ?? []).filter((r) => roleAtLeast(r.role as MemberRole, 'manager'))
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, email, full_name')
      .in(
        'id',
        list.map((r) => r.user_id as string),
      )
    const byId = new Map((profiles ?? []).map((p) => [p.id as string, p]))
    return list.map((r) => ({
      user_id: r.user_id as string,
      role: r.role as MemberRole,
      approval_limit_paisa: (r.approval_limit_paisa as number | null) ?? null,
      name:
        (byId.get(r.user_id as string)?.full_name as string) ||
        (byId.get(r.user_id as string)?.email as string) ||
        'Someone',
    }))
  }, [platform.supabase, tenantId])
  useEffect(() => {
    if (!online) return
    let active = true
    void load()
      .then((list) => active && setPeople(list))
      .catch(() => undefined)
    return () => {
      active = false
    }
  }, [load, online])
  return (
    <SettingsPart
      title="How much each person may approve"
      about="On top of their role. Someone with no limit of their own approves whatever their role allows."
    >
      <div className="sm:col-span-2">
        {!online ? (
          <p className="text-sm text-muted">The team’s limits show when you are online.</p>
        ) : !people ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : (
          <ul className="grid gap-2">
            {people.map((p) => (
              <li
                key={p.user_id}
                className="flex flex-wrap items-center gap-2.5 rounded-xl border border-border px-3 py-2.5"
              >
                <Avatar name={p.name} />
                <span className="min-w-0 flex-1">
                  <b className="font-semibold">{p.name}</b>{' '}
                  <span className="text-sm text-muted capitalize">· {p.role}</span>
                </span>
                <span
                  className={cx(
                    'text-sm',
                    p.approval_limit_paisa === null ? 'text-muted' : 'font-bold text-ink',
                  )}
                >
                  {p.approval_limit_paisa === null
                    ? 'No limit of their own'
                    : `Up to ${money(p.approval_limit_paisa, settings)}`}
                </span>
                {canAdmin && (
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Change the approval limit of ${p.name}`}
                    onClick={() => setEditing(p)}
                  >
                    Change
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      {editing && (
        <LimitDialog
          person={editing}
          onClose={() => setEditing(null)}
          onSaved={(limit) => {
            setPeople(
              (list) =>
                list?.map((p) =>
                  p.user_id === editing.user_id ? { ...p, approval_limit_paisa: limit } : p,
                ) ?? null,
            )
            setEditing(null)
          }}
        />
      )}
    </SettingsPart>
  )
}

function LimitDialog({
  person,
  onClose,
  onSaved,
}: {
  person: Approver
  onClose: () => void
  onSaved: (limit: number | null) => void
}) {
  const { platform } = useSession()
  const { current } = useWorkspace()
  const toast = useToast()
  const [own, setOwn] = useState(person.approval_limit_paisa !== null)
  const [limit, setLimit] = useState<number | null>(person.approval_limit_paisa ?? 5_000_000)
  const [busy, setBusy] = useState(false)
  const save = async () => {
    setBusy(true)
    try {
      const value = own ? limit : null
      await tenantApi(platform.supabase).setApprovalLimit(current!.tenant_id, person.user_id, value)
      toast.success('Approval limit saved')
      onSaved(value)
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }
  const choice = (value: boolean, label: string, sub: string, extra?: ReactNode) => (
    <label
      className={cx(
        'flex cursor-pointer items-center gap-2.5 rounded-xl border px-3 py-2.5',
        own === value ? 'border-primary bg-sell-wash' : 'border-border',
      )}
    >
      <input
        type="radio"
        name="own-limit"
        className="accent-primary"
        checked={own === value}
        onChange={() => setOwn(value)}
      />
      <span className="flex-1">
        <b className="block">{label}</b>
        <span className="text-xs text-muted">{sub}</span>
      </span>
      {extra}
    </label>
  )
  return (
    <Modal
      open
      onClose={onClose}
      title={`Approval limit for ${person.name}`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={busy} disabled={own && limit === null} onClick={() => void save()}>
            Save
          </Button>
        </>
      }
    >
      <div className="grid gap-2 text-sm" role="radiogroup" aria-label="Approval limit">
        {choice(false, 'No limit of their own', `Whatever a ${person.role} may approve`)}
        {choice(
          true,
          'Up to',
          'Above this, someone with a higher limit approves',
          <span className="w-40">
            <MoneyInput
              aria-label="Approves up to"
              prefix="Rs."
              paisa={limit}
              onValue={(paisa) => {
                setLimit(paisa)
                setOwn(true)
              }}
            />
          </span>,
        )}
      </div>
    </Modal>
  )
}
